import React from "react";
import ClockField from "./consolidation/ClockField.jsx";
import { INPUT, PRIMARY, SECONDARY } from "./consolidation/Fields.jsx";
import { clockValue, localDateTime, shiftDate } from "../utils/consolidationPresentation.js";
import "./DriverDepartureSimulation.css";

function dayLabel(day) {
  return new Intl.DateTimeFormat("fr-CA", { dateStyle: "long", timeZone: "UTC" }).format(new Date(`${day}T12:00:00Z`));
}

function DepartureTimeDialog({ action, dispatchDate }) {
  const { stop, label } = action.simulation;
  const timezone = stop.timezone || "America/Toronto";
  const days = [dispatchDate, shiftDate(dispatchDate, 1)];
  const plannedDay = localDateTime(stop.planned_departure, timezone).slice(0, 10);
  const [day, setDay] = React.useState(days.includes(plannedDay) ? plannedDay : dispatchDate);
  const [value, setValue] = React.useState(days.includes(plannedDay) ? stop.planned_departure : "");
  const dialogRef = React.useRef(null);
  const titleId = React.useId();
  const valid = Boolean(value && /Z$|[+-]\d{2}:\d{2}$/.test(value) && Number.isFinite(Date.parse(value)));

  React.useEffect(() => {
    const dialog = dialogRef.current;
    const opener = document.activeElement;
    dialog.showModal();
    dialog.querySelector("input")?.focus();
    return () => { dialog.close(); if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);

  return <dialog ref={dialogRef} aria-labelledby={titleId}
    className="driver-departure-simulation fixed inset-0 m-auto max-h-[calc(100dvh_-_2rem)] w-[calc(100%_-_2rem)] max-w-md overflow-y-auto rounded-[4px] border border-slate-300 bg-white p-4 text-slate-900 shadow-lg backdrop:bg-slate-950/40"
    onCancel={(event) => { event.preventDefault(); action.closeSimulation(); }}>
    <form className="grid min-w-0 gap-3" onSubmit={(event) => { event.preventDefault(); if (valid && !action.pending) action.confirmSimulation(value); }}>
      <h2 id={titleId} className="break-words text-base font-semibold">Départ simulé — {label || "cet arrêt"}</h2>
      <p className="text-sm text-slate-600">Choisissez l’heure du départ pour cette journée de test. Elle sera enregistrée dans Consolidation avec la mention « Départ simulé ».</p>
      <label className="grid gap-1 text-sm">Date du départ
        <select aria-label="Date du départ" className={INPUT} value={day} disabled={action.pending} onChange={(event) => {
          const nextDay = event.target.value;
          setDay(nextDay);
          setValue(clockValue(localDateTime(value, timezone).slice(11), nextDay, timezone));
        }}>{days.map((date, index) => <option key={date} value={date}>{dayLabel(date)}{index ? " — lendemain" : ""}</option>)}</select>
      </label>
      <ClockField label="Heure du départ simulé" value={value} timezone={timezone} baseDate={day} disabled={action.pending} onChange={setValue} />
      {!stop.planned_departure ? <p className="text-xs text-slate-500">Aucune heure prévue : saisissez l’heure souhaitée.</p> : null}
      {action.error ? <p role="alert" className="text-sm text-red-700">{action.error}</p> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={SECONDARY} disabled={action.pending} onClick={action.closeSimulation}>Annuler</button>
        <button type="submit" className={PRIMARY} disabled={action.pending || !valid}>{action.pending ? "Enregistrement…" : "Enregistrer le départ simulé"}</button>
      </div>
    </form>
  </dialog>;
}

export function DriverDepartureTestToggle({ action, dispatchDate }) {
  if (!action.simulationAvailable || !dispatchDate) return null;
  return <button type="button" aria-pressed={action.testMode} disabled={action.pending}
    aria-label={action.testMode ? "Désactiver le mode test" : "Activer le mode test"}
    title={`Mode test ${action.testMode ? "actif" : "inactif"} — ${dayLabel(dispatchDate)}. Les départs simulés sont enregistrés dans Consolidation avec la mention « Départ simulé ».`}
    className={`${action.testMode ? PRIMARY : SECONDARY} driver-departure-test-toggle`}
    onClick={() => action.setTestMode(!action.testMode)}>Test</button>;
}

export default function DriverDepartureSimulation({ action, dispatchDate }) {
  if (!action.simulationAvailable || !dispatchDate || !action.simulation) return null;
  return <DepartureTimeDialog key={`${action.simulation.scopeKey}:${action.simulation.stop.visit_key}`} action={action} dispatchDate={dispatchDate} />;
}
